import { darkTheme, lightTheme } from '@controlrooms/design-tokens';

export const snoozeAlertTheme = Object.fromEntries(
  Object.entries({ light: lightTheme, dark: darkTheme }).map(([key, theme]) => [
    key,
    {
      // Panel Container
      panelBackground: theme.color.surface.cool[2], // #d6dcdd
      panelBorder: theme.color.surface[5], // #b7b5b1
      panelShadow: 'rgba(0, 0, 0, 0.2)',

      // Header
      headerBackground: theme.color.surface.cool[2], // #d6dcdd
      headerShadow: 'rgba(0, 0, 0, 0.15)',
      headerTitle: theme.color.text.core['-primary'], // #1e1e1e
      headerIcon: theme.color.text.core['-primary'], // #1e1e1e
      closeButton: theme.color.text.core['-primary'], // #1e1e1e
      closeButtonHover: theme.color.text.core['-secondary'], // #737373

      // Body
      bodyBackground: '#b8c4c6', // cool surface color not in design tokens
      sectionBackground: theme.color.surface.cool[2], // #d6dcdd
      sectionBorder: '#b8c4c6', // cool surface color not in design tokens
      sectionGap: '16px',

      // Section Headers
      sectionHeaderText: theme.color.text.core['-primary'], // #1e1e1e
      sectionHeaderIcon: theme.color.text.core['-secondary'], // #737373

      // Form Elements
      labelText: theme.color.text.core['-primary'], // #1e1e1e
      labelTextBold: theme.color.text.core['-emphasis'], // #000000
      helperText: theme.color.text.core['-tertiary'], // #a6a6a6

      // Input Fields
      inputBackground: theme.color.surface[2], // #f2f2f2
      inputBorder: theme.color.surface[3], // #d9d7d4
      inputText: theme.color.text.core['-primary'], // #1e1e1e
      inputPlaceholder: theme.color.text.core['-tertiary'], // #a6a6a6
      inputFocusBorder: theme.color.focus[3], // #7199a1
      inputShadow: 'rgba(0, 0, 0, 0.1)',
      inputDisabledBackground: theme.color.surface[1], // #f7f7f5
      inputDisabledText: theme.color.text.core['-tertiary'], // #a6a6a6

      // Dropdown
      dropdownBackground: theme.color.surface[2], // #f2f2f2
      dropdownBorder: theme.color.surface[3], // #d9d7d4
      dropdownText: theme.color.text.core['-primary'], // #1e1e1e
      dropdownIcon: theme.color.text.core['-secondary'], // #737373
      dropdownHoverBackground: theme.color.surface[1], // #f7f7f5

      // Buttons
      buttonPrimaryBackground: theme.color.focus[3], // #7199a1
      buttonPrimaryBorder: theme.color.focus[3], // #7199a1
      buttonPrimaryText: theme.color.surface[0], // #ffffff
      buttonPrimaryHoverBackground: theme.color.focus[4], // #5a8189
      buttonPrimaryShadow: 'rgba(0, 0, 0, 0.4)',

      buttonSecondaryBackground: theme.color.surface[1], // #f9f9f9
      buttonSecondaryBorder: theme.color.surface[2], // #e5e4e3
      buttonSecondaryText: theme.color.text.core['-primary'], // #1e1e1e
      buttonSecondaryHoverBackground: theme.color.surface[0], // #ffffff
      buttonSecondaryShadow: 'rgba(0, 0, 0, 0.2)',

      buttonDisabledBackground: theme.color.surface[2], // #f2f2f2
      buttonDisabledBorder: theme.color.surface[3], // #d9d7d4
      buttonDisabledText: theme.color.text.core['-tertiary'], // #a6a6a6

      // Checkbox
      checkboxBackground: theme.color.surface[1], // #f7f7f5
      checkboxBorder: theme.color.surface[3], // #d9d7d4
      checkboxCheckedBackground: theme.color.focus[5], // #c3dbe0
      checkboxAccent: theme.color.focus[3], // #7199a1
      checkboxText: theme.color.text.core['-primary'], // #1e1e1e

      // Alert Ticket Card
      ticketCardBackground: theme.color.surface[0], // #ffffff
      ticketCardBorder: theme.color.surface[3], // #d9d7d4
      ticketCardShadow: 'rgba(0, 0, 0, 0.2)',
      ticketBadgeBackground: theme.color.surface[4], // #cbc9c7
      ticketBadgeText: theme.color.text.core['-emphasis'], // #000000
      ticketTitleText: theme.color.text.core['-primary'], // #1e1e1e
      ticketMetaText: theme.color.text.core['-primary'], // #1e1e1e

      // Tag Display
      tagBackground: theme.color.surface[2], // #e5e4e3
      tagText: theme.color.text.core['-emphasis'], // #000000
      tagBorder: theme.color.surface[3], // #d9d7d4

      // Note Tags (matches annotation panel)
      noteTagBackground: theme.color.surface[2], // #e5e4e3 - default tag background
      noteTagBackgroundAlt: theme.color.surface[3], // #d9d7d4 - alternate tag background

      // Activity List
      activityListBackground: '#b8c4c6', // cool surface color not in design tokens
      activityItemBackground: theme.color.surface[1], // #f7f7f5
      activityItemBorder: theme.color.surface[3], // #d9d7d4
      activityAuthorText: theme.color.text.core['-emphasis'], // #000000
      activityTimestamp: theme.color.text.core['-tertiary'], // #a6a6a6
      activityText: theme.color.text.core['-primary'], // #1e1e1e
      activityMetaText: theme.color.text.core['-primary'], // #1e1e1e

      // Empty State
      emptyStateText: theme.color.text.core['-secondary'], // #737373

      // Footer
      footerBackground: theme.color.surface.cool[2], // #d6dcdd
      footerBorder: '#b8c4c6', // cool surface color not in design tokens
      footerShadow: 'rgba(0, 0, 0, 0.15)',

      // Scrollbar
      scrollbarBackground: 'rgba(255, 255, 255, 0.75)',
      scrollbarGrabber: theme.color.focus[3], // #7199a1
      scrollbarShadow: 'rgba(0, 0, 0, 0.35)',

      // Error States
      errorText: theme.color.dataVis.alert['limit-2'], // Red color for errors
      errorBorder: theme.color.dataVis.alert['limit-2'],
      errorBackground: theme.color.surface[1], // #f7f7f5

      // Subheader / Section Dividers
      subheaderText: theme.color.text.core['-primary'], // #1e1e1e
      subheaderTextBold: theme.color.text.core['-emphasis'], // #000000
      dividerColor: '#b8c4c6', // cool surface color not in design tokens

      // Info Icon
      infoIcon: theme.color.text.core['-secondary'], // #737373
      infoIconHover: theme.color.text.core['-primary'], // #1e1e1e

      // Loading State
      loadingSpinner: theme.color.focus[3], // #7199a1
      loadingBackground: 'rgba(255, 255, 255, 0.8)',
    },
  ]),
);
